import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import loadingSvg from "../../../images/bouncing-circles.svg";
import {
  PromotionImpactDataItem,
  updateExperinemtForewardForecastPageNumber,
} from "../../../store/resultVisualizationExperimentation";
import { Pagination } from "../../Pagination";
import { resultVisulizationExperimentDefaultGroupBy } from "../../../api/forecastPageApi";
import { BacktestingResultsProps } from "../../../store/foreCastBackTestingDataSlice";
import ForwardForecastTable from "../BacktestingResultTable/ForwardForecastTable";
import Linechart, { ChartAnnotation } from "../../chart/ScatterLine/lineChart";
import AnnotationLegend from "../../chart/ScatterLine/annotationLegends";
import RankDropdownForward from "./RankDropDown";
import { isValidBackTestingChart } from "../../../utils/functions";

interface ChartPoint {
  x: string;
  y: number;
}

interface OutputData {
  promoDiscount: number;
  maxDiscount: number;
  weightedAveragePromotionSales: number;
  minDiscount: number;
  periodWeekNumber: string;
}

interface ChartSeries {
  type: "line" | "bar" | "scatter";
  label: string;
  data: ChartPoint[];
  borderColor?: string;
  backgroundColor?: string;
  borderWidth?: number;
  fill?: boolean;
  pointRadius?: number;
  yAxisID: string;
  pointStyle: string;
}

function ResultVisulizationExperimentForwardForecast({
  widthColumn,
  isResultVisulizationPage,
}: BacktestingResultsProps) {
  const dispatch = useDispatch();
  const forecastFilterExp = useSelector(
    (state: RootState) => state.resultVisualizationExpFilter
  );

  const userState = useSelector((state: RootState) => state.user);
  const [toolTipData, setToolTipData] = useState<any>([]);
  const [chartData, setChartData] = useState<ChartSeries[]>([]);
  const [annotation, setAnnotation] = useState<ChartAnnotation[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [visibleAnnotations, setVisibleAnnotations] = useState<{
    [key: string]: boolean;
  }>({});
  const chartRef = useRef<any>(null);
  const [selectedDataShownOptionList] = useState<string[]>(
    resultVisulizationExperimentDefaultGroupBy
  );

  const payload: any = {
    shortTermMidTermCode: forecastFilterExp?.term.toString(),
    brandName: forecastFilterExp?.brand,
    subBrandName: forecastFilterExp?.subBrand,
    productLineName: forecastFilterExp?.productLine,
    productGroupName: forecastFilterExp?.productGroup,
    materialGlobalTradeItemNumberDescription: forecastFilterExp?.planningItem,
    packageSizeName: forecastFilterExp?.size,
    productCategoryName: forecastFilterExp?.category,
    eachUPC: forecastFilterExp?.eachUPC,
    portfolioGroupName: forecastFilterExp?.mg4PortfolioGroup,
    packageName: forecastFilterExp?.count,
    packTypeName: forecastFilterExp.ppg,
    keyAccount: forecastFilterExp?.keyAccount,
    customerGlobalName: forecastFilterExp?.global,
    customerSectorNumber: forecastFilterExp?.customerSector,
    customerRegionName: forecastFilterExp?.customerRegion,
    customerGroupName: forecastFilterExp?.customerGroup,
    customerBusinessUnitName: forecastFilterExp?.businessUnit,
    customerMarketUnitName: forecastFilterExp?.marketUnit,
    ibpDemandGroup: forecastFilterExp?.ibpDemandGroup,
    customerTradeSegmentDescription: forecastFilterExp?.cg1TrageSegment,
    salesSectorName: forecastFilterExp?.salesSector,
    salesRegionName: forecastFilterExp?.salesRegion,
    salesZoneName: forecastFilterExp?.zone,
    salesCountryCode: forecastFilterExp?.salesCountry,
    salesOrganizationCode: forecastFilterExp?.businessOrg,
    salesDivisionName: forecastFilterExp?.division,
    forecastEngineClusterId: forecastFilterExp?.clusters,
    rank: forecastFilterExp?.rankForward?.map(Number),
    forecastModuleCode: forecastFilterExp?.ac1ac2,
    forecastEngineSegmentationId: forecastFilterExp?.segment,
    packageTypeName: forecastFilterExp?.container,
    sector: userState?.currentSector?.sector,
    market: userState?.currentSector?.market,
    // LATAM Key
    flavorName: forecastFilterExp?.flavourNames,
    customerChannelName: forecastFilterExp?.channels,
    customerDemandGroup: forecastFilterExp?.demandGroups,
    customerSubChannelName: forecastFilterExp?.subChannels,
    customerRegionalCluster: forecastFilterExp?.regionalClusters,
    locationGlobalName: forecastFilterExp?.globalCode,
    locationSectorName: forecastFilterExp?.locationSector,
    locationCountryName: forecastFilterExp?.locationCountry,
    planningLocationId: forecastFilterExp?.planningLocationId,
    locationRegionName: forecastFilterExp?.regionCode,
    // PFNA key
    alternateIBPDemandGroup: forecastFilterExp?.alternateIBPDemandGroup,
  };
  const forwardData = useSelector(
    (state: RootState) => state.ffChart.promotionImpactData
  );

  const holidayData = useSelector(
    (state: RootState) => state.ffChart.holidaysData
  );

  const filter = useSelector(
    (state: RootState) => state.resultVisualizationFilter
  );

  function getSortedUniquePeriodWeekNumbers(
    table: any[],
    chart: any[],
    holidays: any
  ): string[] {
    const uniquePeriodWeekNumbers = new Set<string>([
      ...(table?.map((item) => item?.periodWeekNumber) ?? []),
      ...(chart?.map((item) => item?.periodWeekNumber) ?? []),
      ...Object.keys(holidays ?? {}),
    ]);

    const periodWeekArray = Array.from(uniquePeriodWeekNumbers);

    return periodWeekArray;
  }

  const extractChartData = (chartData: any): ChartSeries[] => {
    const statisticalForecastProductionData: ChartPoint[] = chartData
      ?.filter(
        (item: any) =>
          item.experimentId === "ProductionRun" &&
          item.statisticalForecast !== 0
      )
      .map((item: any) => ({
        x: item.periodWeekNumber,
        y: item.statisticalForecast,
      }));

    const statisticalForecastRankData = (rank: number): ChartPoint[] =>
      chartData
        ?.filter(
          (item: any) =>
            item.rank === rank &&
            item.statisticalForecast !== 0 &&
            item.experimentId !== "ProductionRun"
        )
        .map((item: any) => ({
          x: item.periodWeekNumber,
          y: item.statisticalForecast,
        }));

    const historicalShipmentsData: ChartPoint[] = chartData
      ?.filter((item: any) => item.historicalShipment !== 0)
      .map((item: any) => ({
        x: item.periodWeekNumber,
        y: item.historicalShipment,
      }));

    const avgPromoDiscountData: ChartPoint[] = chartData
      ?.filter(
        (item: any) =>
          item.weightedAveragePromotionSales !== 0 &&
          item.experimentId === "ProductionRun"
      )
      .map((item: any) => ({
        x: item.periodWeekNumber,
        y: item.weightedAveragePromotionSales,
      }));

    const previousYearData: ChartPoint[] = chartData?.map((item: any) => ({
      x: item.periodWeekNumber,
      y: item.previousYear,
    }));

    const datasets: ChartSeries[] = [
      {
        type: "line",
        label: "Statistical Forecast Production",
        data: statisticalForecastProductionData,
        borderColor: "#B992EB",
        backgroundColor: "rgba(255, 165, 0, 0.2)",
        borderWidth: 2,
        pointStyle: "line",
        fill: false,
        yAxisID: "y",
      },
      {
        type: "line",
        label: "Statistical Forecast Rank 1",
        data: statisticalForecastRankData(1),
        borderColor: "#33B1E8",
        backgroundColor: "rgba(255, 165, 0, 0.2)",
        borderWidth: 2,
        fill: false,
        pointStyle: "line",
        yAxisID: "y",
      },
      {
        type: "line",
        label: "Statistical Forecast Rank 2",
        data: statisticalForecastRankData(2),
        borderColor: "#6E3BB2",
        backgroundColor: "rgba(255, 165, 0, 0.2)",
        borderWidth: 2,
        fill: false,
        pointStyle: "line",
        yAxisID: "y",
      },
      {
        type: "line",
        label: "Statistical Forecast Rank 3",
        data: statisticalForecastRankData(3),
        borderColor: "#994900",
        backgroundColor: "rgba(255, 165, 0, 0.2)",
        borderWidth: 2,
        fill: false,
        pointStyle: "line",
        yAxisID: "y",
      },
      {
        type: "line",
        label: "Historical Shipments (Adjusted)",
        data: historicalShipmentsData,
        borderColor: "#FFA800",
        backgroundColor: "rgba(185, 146, 235, 0.2)",
        borderWidth: 2,
        fill: false,
        yAxisID: "y",
        pointStyle: "line",
      },
      {
        type: "scatter",
        label: "Weighted Promo Discount",
        data: avgPromoDiscountData,
        backgroundColor: "#DAB9C9",
        borderColor: "#DAB9C9",
        borderWidth: 2,
        pointRadius: 5,
        yAxisID: "y1",
        pointStyle: "circle",
      },
      {
        type: "bar",
        label: "Previous Year Shipments",
        data: previousYearData,
        backgroundColor: "#A35079",
        borderColor: "#A35079",
        borderWidth: 2,
        pointRadius: 6,
        yAxisID: "y",
        pointStyle: "rect",
      },
    ];

    const allowedRank = filter?.rankForward ?? [];
    const filterDatasets = datasets.filter((dataset) => {
      const name = dataset.label || "";

      // Try to extract rank from name
      const match = name.match(/Rank\s*:?(\d)/i);
      const rank = match ? match[1] : null;

      // Include if:
      // - no rank mentioned in the name (rank is null), OR
      // - rank is in allowed list
      return rank === null || allowedRank.includes(rank);
    });

    return filterDatasets;
  };
  const holidaySpecificColors: Record<string, string> = {
    "Super Bowl": "#FF4500",
    Thanksgiving: "#FF6347",
    "New Year's Day": "#1E90FF",
    Easter: "#FF69B4",
    "Quarter 1 Close": "#2E8B57",
    "Winter Holiday (Q4 Close)": "#8A2BE2",
    "Labor Day (Q3 Close)": "#FFD700",
    "Memorial Day": "#DC143C",
    "Quarter 2 Close": "#20B2AA",
    "Independence Day": "#FF4500",
    "Cinco De Mayo": "#32CD32",
  };

  const getRandomColor = (): string => {
    const array = new Uint8Array(3);
    crypto.getRandomValues(array);
    const [r, g, b] = array;
    return `rgba(${r}, ${g}, ${b}, 1)`;
  };

  const getHolidayColor = (
    holiday: string,
    colorMap: Record<string, string>
  ): string => {
    if (holidaySpecificColors[holiday]) {
      return holidaySpecificColors[holiday];
    }

    if (!colorMap[holiday]) {
      colorMap[holiday] = getRandomColor();
    }

    return colorMap[holiday];
  };

  const mapHolidaysToAnnotations = (
    holidayData: Record<string, string[]>,
    forwardData: PromotionImpactDataItem[]
  ): { annotations: ChartAnnotation[] } => {
    const annotations: ChartAnnotation[] = [];
    const holidayColorMap: Record<string, string> = {}; // To track color for each period/week
    let colorIndex = 0;

    const holidayColors = [
      "#FF5733",
      "#33FF57",
      "#3357FF",
      "#F39C12",
      "#8E44AD",
      "#1ABC9C",
      "#E74C3C",
    ];

    Object.keys(holidayData).forEach((key) => {
      const regex = /([^\d\s]+)\s(\d{4})$/;
      const match = regex.exec(key);

      let period = key;
      let year = "";

      if (match) {
        period = match[1];
        year = match[2];
      }

      const annotationValue = `${period} ${year}`.trim();

      const hasPeriodWeekNumber = forwardData?.some(
        (item: any) => item.periodWeekNumber === annotationValue
      );
      if (!hasPeriodWeekNumber) {
        return;
      }
      let holidayColor = holidayColorMap[annotationValue];
      if (!holidayColor) {
        holidayColor = holidayColors[colorIndex % holidayColors.length];
        colorIndex++;
      }

      holidayData[key]?.forEach((holiday) => {
        annotations.push({
          type: "line",
          borderColor: holidayColor,
          borderWidth: 2,
          value: annotationValue,
          scaleID: "x",
          label: {
            backgroundColor: holidayColor,
            display: true,
            color: "#fff",
            content: `${holiday} (${year})`.trim(),
          },
        });
      });
    });

    return { annotations };
  };

  const { annotations } = useMemo(
    () => mapHolidaysToAnnotations(holidayData, forwardData),
    [holidayData, forwardData]
  );

  const resetZoom = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    } else {
      console.error("Chart ref is not available");
    }
  }, [chartRef]);

  function extractToolTipData(data: PromotionImpactDataItem[]): OutputData[] {
    const seen = new Set();
    return data
      ?.map((item) => ({
        promoDiscount: item.promoDiscount ?? 0,
        maxDiscount: item.maxPromotionImpact ?? 0,
        weightedAveragePromotionSales: item.weightedAveragePromotionSales ?? 0,
        minDiscount: item.minPromotionImpact ?? 0,
        periodWeekNumber: item.periodWeekNumber ?? "N/A",
      }))
      .filter((tooltip) => tooltip.weightedAveragePromotionSales !== 0) // Exclude items with promoDiscount = 0
      .filter((tooltip) => {
        const key = `${tooltip.periodWeekNumber},${tooltip.promoDiscount},${tooltip.maxDiscount},${tooltip.weightedAveragePromotionSales},${tooltip.minDiscount}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
  }

  useEffect(() => {
    const labelsTemp = getSortedUniquePeriodWeekNumbers(
      forwardData,
      resultVisulizationExperimentForwardForecastDataSlice?.backTestingTableData,
      holidayData
    );
    setLabels(labelsTemp);
    setChartData(extractChartData(forwardData));
    setAnnotation(annotations);
    setToolTipData(extractToolTipData(forwardData));
  }, [forwardData, holidayData]);

  const resultVisualizationExperimentationData = useSelector(
    (state: RootState) => state.resultVisualizationExperimentationData
  );

  const loader = useSelector(
    (state: RootState) => state.resultVisualizationData
  );

  const resultVisulizationExperimentForwardForecastDataSlice = useSelector(
    (state: RootState) =>
      state.resultVisulizationExperimentForwardForecastDataSlice
  );

  const resultVisulizationExperimentForwardForecastDataLoadingSlice =
    useSelector(
      (state: RootState) =>
        state.resultVisulizationExperimentForwardForecastDataLoadingSlice
    );

  const resultVisulizationForewardForecastPagination = useSelector(
    (state: RootState) => state.resultVisulizationForewardForecastPagination
  );

  const forwardForecastChartData = useSelector(
    (state: RootState) => state.ffChart
  );

  const renderBackTestingTableHtml = (): ReactElement => {
    return (
      <>
        {resultVisulizationExperimentForwardForecastDataSlice?.isTableDataLoading ? (
          <div className="flex justify-center">
            <img
              src={loadingSvg}
              alt="loading"
              className="mt-10"
              width={40}
              height={40}
            />
          </div>
        ) : (
          <div className="bg-ternary-bg rounded p-2">
            {chartData &&
            chartData?.length > 0 &&
            labels &&
            labels?.length &&
            isValidBackTestingChart(chartData) ? (
              <div className="flex">
                <Linechart
                  chartData={{ datasets: chartData }}
                  chartRef={chartRef}
                  labels={labels}
                  chartAnnotations={annotation.filter(
                    (a) => visibleAnnotations[a.value] !== false
                  )}
                  customTooltipData={toolTipData}
                />
                <div className="overscroll-contain h-06">
                  <AnnotationLegend
                    legendItems={annotation}
                    labelOrder={labels}
                    onVisibilityChange={setVisibleAnnotations}
                  />
                </div>
              </div>
            ) : (
              <div className="bg-ternary-bg rounded p-2">
                <div className="flex justify-center">
                  <p className="p-2 m-1">
                    No Data to show or no options selected, please choose from
                    the above filters.
                  </p>
                </div>
              </div>
            )}
            <ForwardForecastTable
              tableHeader={
                resultVisulizationExperimentForwardForecastDataSlice?.tableHeader
              }
              chartData={forwardForecastChartData?.promotionImpactData}
              backTestingTableData={
                resultVisulizationExperimentForwardForecastDataSlice?.backTestingTableData
              }
              ffPayloadForExcel={{
                ...payload,
                pageNumber: 1,
                pageSize: 1000,
                groupBy:
                  userState?.currentSector?.sector === "LATAM"
                    ? [
                        "ForecastEngineClusterId",
                        "BusinessReportingMaterialId",
                        "BusinessReportingCustomerId",
                        "BusinessReportingLocationId",
                      ]
                    : [
                        "ForecastEngineClusterId",
                        "MaterialGlobalTradeItemNumberDescription",
                        "MaterialGlobalTradeItemNumber",
                        "DemandPlanningKeyAccountId",
                        "DemandPlanningKeyAccountName",
                        "SalesRegionName",
                      ],
              }}
              isResultVisulizationPage={isResultVisulizationPage}
              resultVisulizationTableData={
                forwardForecastChartData.promotionImpactData
              }
              isExperimentationPage={true}
            />
          </div>
        )}
      </>
    );
  };

  const renderBackTestingHtml = (): ReactElement => {
    const isDataEmpty =
      !resultVisulizationExperimentForwardForecastDataLoadingSlice?.isDataLoading &&
      resultVisulizationExperimentForwardForecastDataSlice?.backTestingTableData
        ?.length === 0 &&
      resultVisualizationExperimentationData?.chartData?.length === 0;

    if (isDataEmpty) {
      if (loader) {
        return (
          <div className="bg-ternary-bg rounded p-2">
            <div className="flex justify-center">
              <p className="p-2 m-1">
                No Data to show or no options selected, please choose from the
                above filters.
              </p>
            </div>
          </div>
        );
      } else {
        return (
          <div className="flex justify-center">
            <img
              src={loadingSvg}
              alt="loading"
              className="mt-10"
              width={40}
              height={40}
            />
          </div>
        );
      }
    }

    return (
      <>
        <div className="bg-ternary-bg rounded">
          {renderBackTestingTableHtml()}
        </div>
      </>
    );
  };

  const handlePageNumberUpdateEvent = (currentPageNumber: number) => {
    dispatch(
      updateExperinemtForewardForecastPageNumber({
        pageLimit: resultVisulizationForewardForecastPagination?.pageLimit,
        pageNumber: currentPageNumber,
        totalPages: resultVisulizationForewardForecastPagination?.totalPages,
        isDataShownClicked: false,
        groupBySelectedField: selectedDataShownOptionList,
      })
    );
  };

  return (
    <div className="p-5 bg-secondary-bg mt-4 rounded">
      <div className="flex justify-between">
        <div className="flex mb-2">
          <div className="pl-2">
            <h6 className="text-lg font-bold mb-4 mt-2 pt-2">
              Forward Forecast Results
            </h6>
          </div>
          <div className="pl-2">
            <RankDropdownForward rankType="rankForward" />
          </div>
        </div>

        {!resultVisulizationExperimentForwardForecastDataLoadingSlice?.isDataLoading ||
        resultVisulizationExperimentForwardForecastDataSlice
          ?.backTestingTableData?.length > 0 ? (
          <div>
            {/* <button onClick={handleSettingModal}>
              <img
                className="mr-5"
                width={20}
                height={20}
                src={settingsIcon}
                alt="settingsIcon"
              />
            </button> */}
            {/* <button onClick={handleOpen}>
              <img src={enlargeButton} alt="zoom" />
            </button> */}
          </div>
        ) : null}
      </div>
      <>
        {renderBackTestingHtml()}
        <div className="flex justify-center">
          {resultVisulizationExperimentForwardForecastDataSlice
            ?.backTestingTableData?.length > 0 && (
            <div className="pt-2">
              <Pagination
                totalPages={
                  resultVisulizationForewardForecastPagination?.totalPages
                }
                activePageNumber={
                  resultVisulizationForewardForecastPagination?.pageNumber
                }
                updateBackTestingPageNumber={(data) =>
                  handlePageNumberUpdateEvent(data)
                }
              />
            </div>
          )}
        </div>
      </>
    </div>
  );
}

export default ResultVisulizationExperimentForwardForecast;
